import { useHandler } from "@redotech/react-util/hook";
import { useCallback, useRef } from "react";
import { useLocalStorage } from "usehooks-ts";

const DEFAULT_LEFT_PANEL_WIDTH = 320;
const DEFAULT_RIGHT_PANEL_WIDTH = 320;
const MIN_PANEL_WIDTH = 240;
const MAX_PANEL_WIDTH = 480;

function clampWidth(width: number): number {
  return Math.min(Math.max(width, MIN_PANEL_WIDTH), MAX_PANEL_WIDTH);
}

export interface UseSupportTicketViewLayoutResult {
  leftPanelOpen: boolean;
  setLeftPanelOpen: (open: boolean) => void;
  toggleLeftPanel: () => void;
  rightPanelOpen: boolean;
  setRightPanelOpen: (open: boolean) => void;
  toggleRightPanel: () => void;
  leftPanelWidth: number;
  rightPanelWidth: number;
  handleLeftResize: (delta: number) => void;
  handleRightResize: (delta: number) => void;
  handleLeftResizeEnd: () => void;
  handleRightResizeEnd: () => void;
}

export function useSupportTicketViewLayout(): UseSupportTicketViewLayoutResult {
  const [leftPanelOpen, setLeftPanelOpen] = useLocalStorage<boolean>(
    "redo.support-v2.conversation-detail.left-panel-open",
    true,
  );

  const [rightPanelOpen, setRightPanelOpen] = useLocalStorage<boolean>(
    "redo.support-v2.conversation-detail.right-panel-open",
    true,
  );

  const [leftPanelWidth, setLeftPanelWidth] = useLocalStorage<number>(
    "redo.support-v2.conversation-detail.left-panel-width",
    DEFAULT_LEFT_PANEL_WIDTH,
  );

  const [rightPanelWidth, setRightPanelWidth] = useLocalStorage<number>(
    "redo.support-v2.conversation-detail.right-panel-width",
    DEFAULT_RIGHT_PANEL_WIDTH,
  );

  const leftWidthRef = useRef(leftPanelWidth);
  leftWidthRef.current = leftPanelWidth;

  const rightWidthRef = useRef(rightPanelWidth);
  rightWidthRef.current = rightPanelWidth;

  const toggleLeftPanel = useHandler(() => setLeftPanelOpen(!leftPanelOpen));
  const toggleRightPanel = useHandler(() => setRightPanelOpen(!rightPanelOpen));

  const handleLeftResize = useCallback(
    (delta: number) => {
      leftWidthRef.current = clampWidth(leftWidthRef.current + delta);
      setLeftPanelWidth(leftWidthRef.current);
    },
    [setLeftPanelWidth],
  );

  const handleRightResize = useCallback(
    (delta: number) => {
      rightWidthRef.current = clampWidth(rightWidthRef.current - delta);
      setRightPanelWidth(rightWidthRef.current);
    },
    [setRightPanelWidth],
  );

  const handleLeftResizeEnd = useCallback(() => {
    setLeftPanelWidth(leftWidthRef.current);
  }, [setLeftPanelWidth]);

  const handleRightResizeEnd = useCallback(() => {
    setRightPanelWidth(rightWidthRef.current);
  }, [setRightPanelWidth]);

  return {
    leftPanelOpen,
    setLeftPanelOpen,
    toggleLeftPanel,
    rightPanelOpen,
    setRightPanelOpen,
    toggleRightPanel,
    leftPanelWidth,
    rightPanelWidth,
    handleLeftResize,
    handleRightResize,
    handleLeftResizeEnd,
    handleRightResizeEnd,
  };
}
